import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { useBusiness } from './BusinessContext';

interface UnreadOpportunitiesContextType {
  unreadCount: number;
  isLoading: boolean;
  refetch: () => Promise<void>;
  markAsRead: (opportunityId: number) => void;
  incrementCount: () => void;
}

const UnreadOpportunitiesContext = createContext<UnreadOpportunitiesContextType | undefined>(undefined);

export function UnreadOpportunitiesProvider({ children }: { children: ReactNode }) {
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const { selectedBusiness } = useBusiness();

  const fetchUnreadCount = async () => {
    if (!selectedBusiness?.id || !supabase) {
      setUnreadCount(0);
      setIsLoading(false);
      return;
    }

    try {
      const { count, error } = await supabase
        .from('pipeline_opportunities')
        .select('*', { count: 'exact', head: true })
        .eq('business_id', selectedBusiness.id)
        .eq('is_unread', true);

      if (error) {
        console.error('Error fetching unread opportunities count:', error);
        setUnreadCount(0);
      } else {
        console.log('Fetched unread opportunities count:', count);
        setUnreadCount(count || 0);
      }
    } catch (error) {
      console.error('Error fetching unread opportunities count:', error);
      setUnreadCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  // Manual function to mark an opportunity as read and update count immediately
  const markAsRead = (opportunityId: number) => {
    console.log('Marking opportunity as read:', opportunityId);
    // Optimistically update the count
    setUnreadCount(prev => Math.max(0, prev - 1));
    // Also refetch to ensure accuracy
    setTimeout(() => {
      fetchUnreadCount();
    }, 100);
  };

  // Manual function to increment count when a new opportunity is created
  const incrementCount = () => {
    console.log('Incrementing unread opportunities count');
    setUnreadCount(prev => prev + 1);
    // Also refetch to ensure accuracy
    setTimeout(() => {
      fetchUnreadCount();
    }, 100);
  };

  // Fetch count on mount and when business changes
  useEffect(() => {
    fetchUnreadCount();
  }, [selectedBusiness?.id]);

  // Set up real-time subscription for changes
  useEffect(() => {
    if (!selectedBusiness?.id || !supabase) return;

    console.log('Setting up real-time subscription for unread opportunities context');

    const channel = supabase
      .channel(`unread-opportunities-context-${selectedBusiness.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'pipeline_opportunities',
          filter: `business_id=eq.${selectedBusiness.id}`,
        },
        (payload) => {
          console.log('New opportunity inserted:', payload);
          // For new opportunities, optimistically increment count if it's unread
          const newRecord = payload.new as any;
          if (newRecord?.is_unread !== false) {
            console.log('Incrementing unread count for new opportunity');
            setUnreadCount(prev => prev + 1);
          }
          // Also refetch to ensure accuracy
          setTimeout(() => {
            fetchUnreadCount();
          }, 100);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'pipeline_opportunities',
          filter: `business_id=eq.${selectedBusiness.id}`,
        },
        (payload) => {
          console.log('Opportunity updated:', payload);
          const oldRecord = payload.old as any;
          const newRecord = payload.new as any;
          
          // Check if is_unread status changed
          if (oldRecord?.is_unread !== newRecord?.is_unread) {
            console.log('Unread status changed, refetching count');
            setTimeout(() => {
              fetchUnreadCount();
            }, 100);
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'pipeline_opportunities',
          filter: `business_id=eq.${selectedBusiness.id}`,
        },
        (payload) => {
          console.log('Opportunity deleted:', payload);
          const deletedRecord = payload.old as any;
          if (deletedRecord?.is_unread !== false) {
            console.log('Decrementing unread count for deleted unread opportunity');
            setUnreadCount(prev => Math.max(0, prev - 1));
          }
          // Also refetch to ensure accuracy
          setTimeout(() => {
            fetchUnreadCount();
          }, 100);
        }
      )
      .subscribe((status) => {
        console.log('Unread opportunities context subscription status:', status);
      });

    return () => {
      console.log('Cleaning up unread opportunities context subscription');
      supabase.removeChannel(channel);
    };
  }, [selectedBusiness?.id]);

  return (
    <UnreadOpportunitiesContext.Provider 
      value={{
        unreadCount,
        isLoading,
        refetch: fetchUnreadCount,
        markAsRead,
        incrementCount,
      }}
    >
      {children}
    </UnreadOpportunitiesContext.Provider>
  );
}

export function useUnreadOpportunitiesContext() {
  const context = useContext(UnreadOpportunitiesContext);
  if (context === undefined) {
    throw new Error('useUnreadOpportunitiesContext must be used within an UnreadOpportunitiesProvider');
  }
  return context;
}
